import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "innpact_nl_popup_seen";

export const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch (e) {
      return;
    }
    const t = setTimeout(() => {
      // Não interromper quem está a tratar de uma marcação
      const booking = document.getElementById("marcacoes");
      if (booking) {
        const r = booking.getBoundingClientRect();
        const inView = r.top < window.innerHeight && r.bottom > 0;
        if (inView) return;
      }
      setOpen(true);
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter`, {
        email: form.email.trim(),
        name: form.name.trim() || undefined,
      });
      toast.success(res.data.message || "Subscrição efetuada!");
      dismiss();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível subscrever.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const inputCls =
    "w-full bg-transparent border border-black/15 px-4 py-3 text-sm font-light focus:outline-none focus:border-clay transition-colors duration-300 placeholder:text-black/30";

  return (
    <div
      data-testid="newsletter-popup"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 animate-fade-in"
      onClick={dismiss}
    >
      <div
        className="relative bg-white w-full max-w-3xl grid grid-cols-1 md:grid-cols-2 animate-pop-in overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="newsletter-popup-close"
          onClick={dismiss}
          aria-label="Fechar"
          className="absolute top-4 right-4 z-20 p-1.5 rounded-full bg-white/90 shadow-sm text-black/60 hover:text-black hover:bg-white transition-colors"
        >
          <X size={20} />
        </button>

        <div className="hidden md:block relative">
          <img
            src="/espaco/espaco_entrada.webp"
            alt="INN.PACT — Clínica de Imagem em Leiria: entrada do espaço"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-clay/25" />
        </div>

        <div className="p-8 md:p-10">
          <p className="label-editorial text-clay mb-4">Newsletter INN.PACT</p>
          <h3 className="font-display text-3xl md:text-4xl leading-none tracking-tight">
            Junte-se à <span className="italic text-clay">nossa lista</span>
          </h3>
          <p className="mt-4 text-black/60 font-light text-sm leading-relaxed">
            Receba em primeira mão tendências, dicas de imagem e ofertas exclusivas da INN.PACT.
          </p>
          <form onSubmit={submit} data-testid="newsletter-popup-form" className="mt-6 space-y-3">
            <input
              data-testid="newsletter-popup-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nome (opcional)"
              className={inputCls}
            />
            <input
              data-testid="newsletter-popup-email"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="O seu email"
              className={inputCls}
            />
            <button
              data-testid="newsletter-popup-submit"
              type="submit"
              disabled={submitting}
              className="w-full label-editorial bg-clay text-white px-8 py-3.5 hover:bg-clay-dark transition-colors duration-300 disabled:opacity-50"
            >
              {submitting ? "A subscrever…" : "Subscrever"}
            </button>
          </form>
          <button onClick={dismiss} className="mt-4 text-xs text-black/40 hover:text-black/70 underline">
            Agora não, obrigado
          </button>
        </div>
      </div>
    </div>
  );
};
